import { AlertTriangle, CheckCircle2, Clock, Info } from "lucide-react";
import { fdate, mxn } from "@/lib/format";
import { TIPOS_OT } from "@/lib/arrendamiento";
import { FilasResumen } from "./OtShared";

export const EPS_OT = 0.005;
const VERDE = "border-emerald-200 bg-emerald-50 text-emerald-700";
const ESTADOS = {
  pendiente: ["Pendiente de decisión", "border-amber-200 bg-amber-50 text-amber-800"],
  vencida: ["Fecha límite vencida", "border-rose-200 bg-rose-50 text-rose-700"],
  en_comite: ["Prórroga en Comité", "border-sky-200 bg-sky-50 text-sky-800"],
  ejercida_compra: ["Ejercida · Compra", VERDE],
  ejercida_prorroga: ["Ejercida · Prórroga", VERDE],
  ejercida_devolucion: ["Ejercida · Devolución", VERDE],
  sin_residual: ["Sin valor residual", "border-slate-200 bg-slate-50 text-slate-600"],
};
const TONOS = { rose: "border-rose-200 bg-rose-50 text-rose-800", sky: "border-sky-200 bg-sky-50 text-sky-900",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-800", slate: "border-slate-200 bg-slate-50 text-slate-700" };

export const bienTexto = (b = {}) => [b.descripcion_bien, b.marca, b.modelo, b.anio].filter(Boolean).join(" ")
  + (b.numero_serie ? ` · serie ${b.numero_serie}` : "");
const polizaTexto = (p) => (p ? `${p.aseguradora} · ${p.numero_poliza} · vigente al ${fdate(p.vigencia_hasta)}` : "—");
const rechazada = (s) => ["Rechazado", "Rechazada"].includes(s?.estatus);

export function OtEstadoBadge({ ot }) {
  const [txt, cls] = ESTADOS[ot.estado] || [ot.estado, ESTADOS.sin_residual[1]];
  return (
    <span data-testid="ot-estado-badge" className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cls}`}>
      {txt}{ot.estado === "vencida" ? ` · ${ot.dias_vencidos} días` : ""}
    </span>
  );
}

function Aviso({ tono, icon: Icon, testid, children }) {
  return (
    <div className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${TONOS[tono]}`} data-testid={testid}>
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" /><div>{children}</div>
    </div>
  );
}

function EnComite({ ot }) {
  const p = ot.prorroga || {};
  return (
    <>
      <Aviso tono="sky" icon={Clock} testid="ot-aviso-comite">{ot.bloqueo}</Aviso>
      <FilasResumen testid="ot-resumen-comite" filas={[["Solicitud de prórroga", p.estatus],
        ["Capital a prorrogar (sin IVA)", mxn(p.capital), true], ["Plazo", `${p.plazo} rentas`],
        ["Tasa anual", `${p.tasa_ordinaria} %`], ["Residual de la prórroga", `${p.residual_pct ?? 0} % · ${mxn(p.valor_residual)}`],
        ["Póliza", polizaTexto(p.poliza)], ["Enviada", fdate(p.created_at)]]} />
      <p className="text-[11px] text-slate-500">Si el Comité autoriza, el nuevo crédito se contrata solo (sin desembolso) y el residual
        queda «Prorrogada». Si la rechaza, Comprar, Prorrogar y Devolver se liberan.</p>
    </>
  );
}

function Pendiente({ ot }) {
  const vencida = ot.estado === "vencida";
  const filas = [["Bien", bienTexto(ot.bien)], ["Valor residual", mxn(ot.residual)], ["IVA del residual", mxn(ot.iva_residual)],
    ["Total para comprar (residual + IVA)", mxn(ot.total_residual), true], ["Fecha límite de la opción", fdate(ot.fecha_limite)]];
  if (vencida) filas.push(["Días vencidos", ot.dias_vencidos], [`Mora sobre ${mxn(ot.residual)}`, mxn(ot.mora)],
    ["IVA de la mora", mxn(ot.iva_mora)], ["Mora a pagar primero", mxn(ot.mora_total), true]);
  if (ot.mora_pagada > EPS_OT) filas.push(["Mora del residual ya pagada (+ IVA)", mxn(ot.mora_pagada + ot.iva_mora_pagada)]);
  return (
    <>
      <FilasResumen filas={filas} testid="ot-resumen-pendiente" />
      {vencida && ot.mora_total > EPS_OT && (
        <Aviso tono="rose" icon={AlertTriangle} testid="ot-aviso-mora">
          La fecha límite venció hace {ot.dias_vencidos} días. Antes de ejercer cualquier opción se paga solo la mora del
          residual más IVA (<b>{mxn(ot.mora_total)}</b>) desde «Opción terminal».
        </Aviso>
      )}
      {vencida && ot.mora_total <= EPS_OT && (
        <Aviso tono="emerald" icon={CheckCircle2} testid="ot-aviso-mora-pagada">La mora del residual está cubierta: ya puedes ejercer la opción.</Aviso>
      )}
      {ot.estado === "en_comite" && <EnComite ot={ot} />}
      {ot.estado !== "en_comite" && rechazada(ot.ultima_prorroga) && (
        <Aviso tono="slate" icon={Info} testid="ot-aviso-rechazo">El Comité rechazó la última prórroga: Comprar, Prorrogar y Devolver están liberadas.</Aviso>
      )}
      {!ot.puede_ejercer && (
        <p className="text-[11px] text-slate-500" data-testid="ot-solo-lectura">Solo Mesa de Control o el administrador de la entidad ejercen la opción terminal.</p>
      )}
    </>
  );
}

const DETALLE = {
  compra: (o) => [["Pagado (residual + IVA)", mxn(o.importe), true], ["Residual · IVA", `${mxn(o.valor_residual)} · ${mxn(o.iva_residual)}`],
    ["Recibo del pago", o.recibo]],
  prorroga: (o) => [["Nuevo crédito", o.numero_credito_prorroga, true],
    ["Condiciones", `${o.plazo} rentas · ${o.tasa_ordinaria} % · residual ${mxn(o.valor_residual_prorroga)}`],
    ["Renta (sin IVA)", mxn(o.renta_prorroga)], ["Solicitó · autorizó", `${o.solicitada_por || "—"} · ${o.autorizada_por || "—"}`],
    ["Póliza", polizaTexto(o.poliza)]],
  devolucion: (o) => [["Residual cancelado", mxn(o.valor_residual), true], ["Fecha de entrega", fdate(o.fecha_entrega)],
    ["Recibió", o.recibido_por], ["Estado del bien", o.estado_bien || "—"]],
};
const EFECTO = {
  compra: "Crédito Liquidado; residual «Pagada · opción de compra». El CFDI de la enajenación se emite por separado.",
  prorroga: "Crédito Liquidado; residual «Prorrogada». La prórroga no se cancela manualmente.",
  devolucion: "Crédito Liquidado; residual «Cancelada por devolución». El bien está en «Bienes devueltos» de Cartera.",
};

function Ejercida({ ot }) {
  const o = ot.opcion || {};
  const filas = [["Opción", TIPOS_OT[o.tipo]], ["Fecha", fdate(o.fecha)], ["Folio", o.folio], ...(DETALLE[o.tipo]?.(o) || []),
    ...(o.mora_pagada > EPS_OT ? [["Mora del residual pagada (+ IVA)", mxn(o.mora_pagada + (o.iva_mora_pagada || 0))]] : []),
    ["Registró", o.usuario]];
  return (
    <>
      <FilasResumen filas={filas} testid={`ot-resumen-${o.tipo}`} />
      <Aviso tono="emerald" icon={CheckCircle2} testid="ot-efecto-ejercida">{EFECTO[o.tipo]}</Aviso>
    </>
  );
}

function Historial({ items, onCarta }) {
  if (!items?.length) return null;
  return (
    <div className="space-y-1" data-testid="ot-historial">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Opciones canceladas</p>
      {items.map((h) => (
        <p key={h.id} className="text-xs text-slate-600" data-testid={`ot-historial-${h.id}`}>
          {TIPOS_OT[h.tipo]} del {fdate(h.fecha)} ({h.folio}) · cancelada el {fdate(h.cancelado_fecha)} por {h.cancelado_por}: «{h.cancelado_motivo}»
          {h.tipo === "compra" && (
            <button type="button" className="ml-1 text-primary underline" onClick={() => onCarta(h.folio)} data-testid={`ot-historial-carta-${h.id}`}>carta (cancelada)</button>
          )}
        </p>
      ))}
    </div>
  );
}

export function OtEstadoDetalle({ ot, onCarta }) {
  return (
    <div className="space-y-2">
      {ot.origen && (
        <p className="text-xs text-slate-600" data-testid="ot-origen">Crédito de prórroga del arrendamiento <b>{ot.origen.numero_credito}</b>
          {ot.estado === "sin_residual" ? ": sin valor residual, no tiene opción terminal al final." : "."}</p>
      )}
      {["pendiente", "vencida", "en_comite"].includes(ot.estado) && <Pendiente ot={ot} />}
      {ot.estado?.startsWith("ejercida_") && <Ejercida ot={ot} />}
      <Historial items={ot.historial} onCarta={onCarta} />
    </div>
  );
}
